/**
 * API Constants
 * Centralized API endpoint definitions
 * All API endpoints are registered here for consistency
 * 
 * Platform, Organization, and Branch endpoints are separated into their own files
 * for better organization and maintainability.
 */

import { API_BASE_URL } from './environment'
import { PLATFORM_API_ENDPOINTS } from './api/platform.api.constants'
import { ORGANIZATION_API_ENDPOINTS } from './api/organization.api.constants'

/**
 * API Configuration
 */
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  FULL_BASE_URL: API_BASE_URL,
  TIMEOUT: 15000, // 15 seconds (per-request overrides e.g. select-context may use longer)
}

/**
 * API Headers
 */
export const API_HEADERS = {
  CONTEXT_TOKEN: 'X-Context-Token',
  ORGANIZATION_SLUG: 'X-Organization-Slug',
  BRANCH_SLUG: 'X-Branch-Slug',
  REQUEST_ID: 'X-Request-ID',
}

/**
 * API Endpoints
 * Organized by feature/module for easy maintenance
 */
export const API_ENDPOINTS = {
  // ============================================
  // USERS (me – any authenticated user)
  // ============================================
  USERS: {
    UI_PREFERENCES: '/users/me/ui-preferences',
    UI_PREFERENCES_OPTIONS: '/users/me/ui-preferences/options',
    TABLE_VIEWS: '/users/me/table-views',
    TABLE_VIEW: (viewKey: string) => `/users/me/table-views/${encodeURIComponent(viewKey)}`,
  },

  // ============================================
  // AUTHENTICATION
  // ============================================
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    LOGOUT_ALL_DEVICES: '/auth/logout-all-devices',
    REQUEST_OTP: '/auth/request-otp',
    LOGIN_WITH_OTP: '/auth/login-otp', // Login using OTP
    VERIFY_OTP: '/auth/verify-otp',
    REFRESH_TOKEN: '/auth/refresh-token',
    FORGOT_PASSWORD: '/auth/password/forgot', // Request OTP for password reset
    RESET_PASSWORD: '/auth/password/reset', // Reset password with OTP
    CHANGE_PASSWORD: '/auth/change-password',
    GET_CONTEXTS: '/auth/contexts',
    SELECT_CONTEXT: '/auth/select-context',
    SELECT_STUDENT_CONTEXT: '/auth/select-student-context', // Parent with multiple students: pick one to set activeProfileId
    GET_CONTEXT: '/auth/context', // Get full context data (branch_ids, roles, permissions) from cache
    SWITCH_BRANCH: '/auth/switch-branch',
    UI_PERMISSIONS: '/auth/ui-permissions',
    WORKSPACE_BOOTSTRAP: '/auth/workspace-bootstrap', // Unified endpoint for context + sidebar + permissions (optimized)
    VERIFY_EMAIL: '/auth/verify-email',
    VERIFY_ACCOUNT: '/auth/verify-account',
    CREATE_PASSWORD: '/auth/create-password',
    BRANCH_SELECT: '/auth/branch-select',
    CREATED_BRANCHES: '/auth/created-branches',
    ONBOARDING_COMPLETE_PROFILE: '/auth/onboarding/complete-profile',
    ONBOARDING_STATUS: '/auth/onboarding/status',
    ONBOARDING_DASHBOARD: '/auth/onboarding/dashboard',
  },

  // ============================================
  // NAVIGATION
  // ============================================
  NAVIGATION: {
    USER_CONTEXT: '/navigation/user-context',
    SIDEBAR: '/navigation/sidebar',
    /** GET /navigation/registry?scope=platform|organization|branch — used for runtime menu (e.g. guideUrl) without running generate:navigation */
    REGISTRY: '/navigation/registry',
    /** GET /navigation/route-catalog?scope=... — all menu routes incl. disabled (direct URL guard) */
    ROUTE_CATALOG: '/navigation/route-catalog',
  },

  // ============================================
  // SHARED (Onboarding, etc.)
  // ============================================
  SHARED: {
    SUGGESTIONS: {
      CREATE: '/shared/suggestions',
      MY_LIST: '/shared/suggestions/me',
      GET_ONE: (id: string) => `/shared/suggestions/${id}`,
      LIST: '/shared/suggestions',
      UPDATE_STATUS: (id: string) => `/shared/suggestions/${id}/status`,
    },
    ONBOARDING: {
      INVITATION_FORMS: '/shared/onboarding/invitation-forms',
      INVITE: '/shared/onboarding/invite',
      INVITATIONS: '/shared/onboarding/invitations',
      RESEND_INVITATION: (invitationId: string) => `/shared/onboarding/resend-invitation/${invitationId}`,
      CANCEL_INVITATION: (invitationId: string) => `/shared/onboarding/cancel-invitation/${invitationId}`,
      PENDING_ONBOARDINGS: '/shared/onboarding/onboarding/pending',
      LIFECYCLE_COUNTS: '/shared/onboarding/onboarding/lifecycle-counts',
      ONBOARDING_STATUS: (userId: string) => `/shared/onboarding/onboarding/status/${userId}`,
      APPROVE_ONBOARDING: (userId: string) => `/shared/onboarding/onboarding/approve/${userId}`,
      APPROVE_ONBOARDING_BY_ID: (onboardingId: string) =>
        `/shared/onboarding/onboarding/by-id/${onboardingId}/approve`,
      REJECT_ONBOARDING: (userId: string) => `/shared/onboarding/onboarding/reject/${userId}`,
      REQUEST_CHANGES: (userId: string) => `/shared/onboarding/onboarding/request-changes/${userId}`,
      ASSIGN_HELPER: (userId: string) => `/shared/onboarding/onboarding/assign-helper/${userId}`,
      ADMIN_UPDATE_PROFILE: (userId: string) => `/shared/onboarding/onboarding/admin-update-profile/${userId}`,
      USERS: '/shared/onboarding/users',
      USER_BY_ID: (userId: string) => `/shared/onboarding/users/${userId}`,
      USER_INVITATIONS: (userId: string) => `/shared/onboarding/onboarding/user/${userId}/invitations`,
      DEACTIVATE_USER: (userId: string) => `/shared/onboarding/users/${userId}/deactivate`,
      REACTIVATE_USER: (userId: string) => `/shared/onboarding/users/${userId}/reactivate`,
      CANCEL_PENDING_PLATFORM_INVITATION: (userId: string) =>
        `/shared/onboarding/users/${userId}/pending-invitation`,
      UPDATE_USER_ROLES: (userId: string) => `/shared/onboarding/users/${userId}/roles`,
    },
  },

  // ============================================
  // MENU
  // ============================================
  MENU: {
    PLATFORM: '/platform/menu',
    ORGANIZATION: (orgSlug: string) => `/orgs/${orgSlug}/menu`,
    BRANCH: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/branches/${branchSlug}/menu`,
    SELF: (orgSlug: string) => `/orgs/${orgSlug}/self/menu`,
    ALUMNI: (orgSlug: string) => `/orgs/${orgSlug}/alumni/menu`,
  },

  // ============================================
  // THEME
  // ============================================
  THEME: {
    LIST: '/themes',
    GET_USER_THEME: '/user/theme',
    SET_USER_THEME: '/user/theme',
    GET_ORG_THEME: (orgSlug: string) => `/orgs/${orgSlug}/theme`,
    UPDATE_ORG_THEME: (orgSlug: string) => `/orgs/${orgSlug}/theme`,
  },

  // ============================================
  // LOGO
  // ============================================
  LOGO: {
    GET_ORG_LOGO: (orgSlug: string) => `/orgs/${orgSlug}/logo`,
    UPDATE_ORG_LOGO: (orgSlug: string) => `/orgs/${orgSlug}/logo`,
    DELETE_ORG_LOGO: (orgSlug: string) => `/orgs/${orgSlug}/logo`,
  },

  // ============================================
  // ORGANIZATION
  // ============================================
  ORGANIZATION: {
    GET: (orgSlug: string) => `/orgs/${orgSlug}`,
    LIST: '/orgs',
    CREATE: '/orgs',
    UPDATE: (orgSlug: string) => `/orgs/${orgSlug}`,
    DELETE: (orgSlug: string) => `/orgs/${orgSlug}`,
  },

  // ============================================
  // PUBLIC (Registration, Contact, Demo, Onboarding)
  // ============================================
  PUBLIC: {
    HEALTH: '/public/health',
    PLANS: '/public/plans',
    CONTACT: {
      SUBMIT: '/public/contact',
      LIST: '/public/contact',
      STATS: '/public/contact/stats',
      UPDATE_STATUS: (id: string) => `/public/contact/${id}/status`,
    },
    DEMO_BOOKING: '/public/demo-booking',
    REGISTER_ORG_CONFIG: '/public/register-organization/config',
    REGISTER_ORG_START: '/public/register-organization/start',
    REGISTER_ORG_VERIFY_EMAIL: '/public/register-organization/verify-email',
    REGISTER_ORG_UPDATE_SESSION: '/public/register-organization/update-session',
    REGISTER_ORG_PLANS: '/public/register-organization/plans',
    REGISTER_ORG_SELECT_PLAN: '/public/register-organization/select-plan',
    REGISTER_ORG_CREATE_PAYMENT: '/public/register-organization/payment/create-order',
    REGISTER_ORG_VERIFY_PAYMENT: '/public/register-organization/payment/verify',
    REGISTER_ORG_COMPLETE: '/public/register-organization/complete',
    PINCODE_LOOKUP: (pincode: string) => `/public/address/pincode/${pincode}`,
    PINCODE_VALIDATE: (pincode: string) => `/public/address/pincode/validate/${pincode}`,
    GET_ENUMS: '/public/get-enums-by-filters',
    ONBOARDING: {
      VERIFY_INVITATION: (token: string) => `/shared/onboarding/public/verify-magic-link/${token}`,
      REQUEST_OTP: '/shared/onboarding/public/request-otp',
      VERIFY_OTP: '/shared/onboarding/public/verify-otp',
      ACCEPT_INVITATION: '/shared/onboarding/public/accept-invitation',
      PROFILE_FORM: (token: string) => `/shared/onboarding/public/profile-form/${token}`,
      SUBMIT_PROFILE: (token: string) => `/shared/onboarding/public/submit-profile/${token}`,
      SAVE_DRAFT: (token: string) => `/shared/onboarding/public/save-draft/${token}`,
      ONBOARDING_STATUS: (token: string) => `/shared/onboarding/public/status/${token}`,
      ONBOARDING_DASHBOARD: (userId: string) => `/shared/onboarding/onboarding/user/${userId}/invitations`,
      INVITATIONS_BY_EMAIL: '/shared/onboarding/public/invitations-by-email',
      UPLOAD_DOCUMENT: (token: string) => `/shared/onboarding/public/upload-document/${token}`,
    },
  },

  // ============================================
  // PLATFORM (Platform admin routes)
  // ============================================
  PLATFORM: PLATFORM_API_ENDPOINTS,

  // ============================================
  // ORGANIZATION (Organization admin routes)
  // ============================================
  ORG: ORGANIZATION_API_ENDPOINTS,

}

